import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, Chip, CircularProgress, Divider, Input,
    Option, Select, Sheet, Stack, Tooltip, Typography,
} from '@mui/joy';
import { useNavigate } from 'react-router';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';

import { useAdvertiser } from '../../../AdvertiserContext';
import {
    APIAdsCampaignArchive, APIAdsCampaignPause, APIAdsCampaignResume,
    APIAdsCampaignSubmit, APIAdsCampaigns, adsErrorText,
} from '../../../api_ads';
import AdsAccountBar from '../../../components/Ads/AdsAccountBar';
import AdStatusChip from '../../../components/Ads/AdStatusChip';
import { useAdsAccount } from '../../../components/Ads/useAdsData';
import {
    PRICING_LABELS, STRATEGY_LABELS, can, formatMoney,
} from '../../../components/Ads/adsConstants';


const STATUS_FILTERS = [
    { value: '', label: 'Все' },
    { value: 'draft', label: 'Черновики' },
    { value: 'moderation', label: 'На модерации' },
    { value: 'active,scheduled', label: 'Работают' },
    { value: 'paused,out_of_budget', label: 'Остановлены' },
    { value: 'rejected', label: 'Отклонены' },
    { value: 'completed,archived', label: 'Завершены' },
];


function CounterChip({ label, value, color }) {
    if (!value) return null;
    return (
        <Chip variant="soft" color={color} size="sm">
            {label}: {value}
        </Chip>
    );
}


function CampaignRow({ campaign, onOpen, onAction, busy }) {
    const budgetLeft = Number(campaign.budget_left_today ?? campaign.daily_budget ?? 0);
    const dailyBudget = Number(campaign.daily_budget || 0);
    const spentToday = dailyBudget - budgetLeft;
    const usedPercent = dailyBudget > 0
        ? Math.min(100, Math.round((spentToday / dailyBudget) * 100))
        : 0;

    return (
        <Card
            variant="outlined"
            sx={{
                p: 2, cursor: 'pointer', transition: '.15s',
                '&:hover': { borderColor: 'primary.300', boxShadow: 'sm' },
            }}
            onClick={() => onOpen(campaign.id)}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
            >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography level="title-md" sx={{ fontWeight: 700 }}>
                            {campaign.name}
                        </Typography>
                        <AdStatusChip
                            status={campaign.status}
                            systemPaused={campaign.system_paused}
                            systemPauseReason={campaign.system_pause_reason}
                        />
                    </Stack>

                    <Typography level="body-xs" color="neutral" sx={{ mt: 0.4 }}>
                        {PRICING_LABELS[campaign.pricing_model]} ·{' '}
                        {STRATEGY_LABELS[campaign.bid_strategy] || campaign.bid_strategy} ·{' '}
                        {campaign.groups_count || 0} гр. ·{' '}
                        создана {campaign.date_create
                            ? format(new Date(campaign.date_create), 'd MMM yyyy', { locale: ru })
                            : '—'}
                    </Typography>

                    {campaign.status === 'rejected' && campaign.moderation_comment && (
                        <Alert color="danger" size="sm" sx={{ mt: 1 }}>
                            {campaign.moderation_comment}
                        </Alert>
                    )}
                </Box>

                <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                    <Box sx={{ minWidth: 120 }}>
                        <Typography level="body-xs" color="neutral">Бюджет на сегодня</Typography>
                        <Typography level="body-sm" sx={{ fontWeight: 700 }}>
                            {formatMoney(budgetLeft)} / {formatMoney(dailyBudget)} ₽
                        </Typography>
                        <Box sx={{
                            mt: 0.5, height: 4, borderRadius: 4,
                            bgcolor: 'neutral.softBg', overflow: 'hidden',
                        }}>
                            <Box sx={{
                                width: `${usedPercent}%`, height: '100%',
                                bgcolor: usedPercent >= 100 ? 'warning.400' : 'primary.400',
                            }} />
                        </Box>
                    </Box>

                    <Box sx={{ minWidth: 100 }}>
                        <Typography level="body-xs" color="neutral">Потрачено всего</Typography>
                        <Typography level="body-sm" sx={{ fontWeight: 700 }}>
                            {formatMoney(campaign.spent_total)} ₽
                        </Typography>
                    </Box>

                    <Stack
                        direction="row"
                        spacing={0.5}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {can(campaign.status, 'submit') && (
                            <Tooltip title="Отправить на модерацию" arrow>
                                <Button
                                    size="sm"
                                    variant="soft"
                                    color="primary"
                                    loading={busy === `submit-${campaign.id}`}
                                    onClick={() => onAction('submit', campaign)}
                                >
                                    <SendRoundedIcon fontSize="small" />
                                </Button>
                            </Tooltip>
                        )}
                        {can(campaign.status, 'pause') && (
                            <Tooltip title="Остановить" arrow>
                                <Button
                                    size="sm"
                                    variant="soft"
                                    color="neutral"
                                    loading={busy === `pause-${campaign.id}`}
                                    onClick={() => onAction('pause', campaign)}
                                >
                                    <PauseRoundedIcon fontSize="small" />
                                </Button>
                            </Tooltip>
                        )}
                        {can(campaign.status, 'resume') && (
                            <Tooltip title="Возобновить" arrow>
                                <Button
                                    size="sm"
                                    variant="soft"
                                    color="success"
                                    loading={busy === `resume-${campaign.id}`}
                                    onClick={() => onAction('resume', campaign)}
                                >
                                    <PlayArrowRoundedIcon fontSize="small" />
                                </Button>
                            </Tooltip>
                        )}
                        {can(campaign.status, 'archive') && (
                            <Tooltip title="В архив" arrow>
                                <Button
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    loading={busy === `archive-${campaign.id}`}
                                    onClick={() => onAction('archive', campaign)}
                                >
                                    <Inventory2RoundedIcon fontSize="small" />
                                </Button>
                            </Tooltip>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Card>
    );
}


export default function AdsCampaignsPage() {
    const navigate = useNavigate();
    const { advertiserToken: token } = useAdvertiser();
    const { account, limits, loading: accountLoading, error: accountError, reload: reloadAccount }
        = useAdsAccount();

    const [items, setItems] = useState([]);
    const [counters, setCounters] = useState({});
    const [pages, setPages] = useState(1);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');
    const [hideArchived, setHideArchived] = useState(true);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdsCampaigns(token, {
                status: status || undefined,
                search: search || undefined,
                hideArchived,
                page,
            });
            setItems(res?.items || []);
            setCounters(res?.counters || {});
            setPages(res?.pages || 1);
        } catch (e) {
            setError(adsErrorText(e, 'Не удалось загрузить кампании'));
        } finally {
            setLoading(false);
        }
    }, [token, status, search, hideArchived, page]);

    useEffect(() => { load(); }, [load]);

    // Поиск с задержкой — иначе запрос уходит на каждую букву
    const [searchInput, setSearchInput] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            setSearch(searchInput);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const handleAction = async (action, campaign) => {
        setNotice(null);
        setError(null);
        setBusy(`${action}-${campaign.id}`);
        try {
            if (action === 'submit') {
                const res = await APIAdsCampaignSubmit(token, campaign.id);
                setNotice(res?.auto_approved
                    ? `Кампания «${campaign.name}» одобрена автоматически и запущена`
                    : `Кампания «${campaign.name}» отправлена на модерацию`);
            } else if (action === 'pause') {
                await APIAdsCampaignPause(token, campaign.id);
            } else if (action === 'resume') {
                await APIAdsCampaignResume(token, campaign.id);
            } else if (action === 'archive') {
                await APIAdsCampaignArchive(token, campaign.id);
            }
            await load();
            await reloadAccount();
        } catch (e) {
            // Ошибки валидации при отправке приходят списком в errors —
            // это самая частая ситуация, показываем её целиком.
            const data = e?.response?.data;
            if (Array.isArray(data?.errors)) {
                setError(`Кампания «${campaign.name}» не готова:\n• ${data.errors.join('\n• ')}`);
            } else {
                setError(adsErrorText(e));
            }
        } finally {
            setBusy(null);
        }
    };

    return (
        <Box>
            <AdsAccountBar
                account={account}
                limits={limits}
                loading={accountLoading}
                error={accountError}
            />

            {notice && (
                <Alert color="success" sx={{ mb: 2 }} endDecorator={
                    <Button size="sm" variant="plain" color="success" onClick={() => setNotice(null)}>
                        Скрыть
                    </Button>
                }>
                    {notice}
                </Alert>
            )}

            {error && (
                <Alert color="danger" sx={{ mb: 2, whiteSpace: 'pre-line' }} endDecorator={
                    <Button size="sm" variant="plain" color="danger" onClick={() => setError(null)}>
                        Скрыть
                    </Button>
                }>
                    {error}
                </Alert>
            )}

            {/* Панель фильтров */}
            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="space-between"
                >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Input
                            size="sm"
                            placeholder="Поиск по названию"
                            startDecorator={<SearchRoundedIcon />}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            sx={{ minWidth: 200 }}
                        />
                        <Select
                            size="sm"
                            value={status}
                            onChange={(_, v) => { setStatus(v ?? ''); setPage(1); }}
                            sx={{ minWidth: 160 }}
                        >
                            {STATUS_FILTERS.map((f) => (
                                <Option key={f.value} value={f.value}>{f.label}</Option>
                            ))}
                        </Select>
                        <Button
                            size="sm"
                            variant={hideArchived ? 'soft' : 'outlined'}
                            color="neutral"
                            onClick={() => { setHideArchived((v) => !v); setPage(1); }}
                        >
                            {hideArchived ? 'Архив скрыт' : 'Архив показан'}
                        </Button>
                    </Stack>

                    <Button
                        startDecorator={<AddRoundedIcon />}
                        onClick={() => navigate('/business/new')}
                        disabled={account?.status !== 'active'}
                    >
                        Создать кампанию
                    </Button>
                </Stack>

                {!!Object.keys(counters).length && (
                    <>
                        <Divider sx={{ my: 1.2 }} />
                        <Stack direction="row" spacing={0.8} flexWrap="wrap">
                            <CounterChip label="Черновики" value={counters.draft} color="neutral" />
                            <CounterChip label="На модерации" value={counters.moderation} color="warning" />
                            <CounterChip label="Работают" value={counters.active} color="success" />
                            <CounterChip label="Остановлены" value={counters.paused} color="neutral" />
                            <CounterChip label="Отклонены" value={counters.rejected} color="danger" />
                        </Stack>
                    </>
                )}
            </Sheet>

            {/* Список */}
            {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}>
                    <CircularProgress />
                </Stack>
            ) : items.length === 0 ? (
                <Sheet variant="soft" sx={{ p: 4, borderRadius: 'md', textAlign: 'center' }}>
                    <Typography level="title-md" sx={{ mb: 0.5 }}>
                        {search || status ? 'Ничего не найдено' : 'Кампаний пока нет'}
                    </Typography>
                    <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                        {search || status
                            ? 'Попробуйте изменить фильтры'
                            : 'Создайте первую кампанию, чтобы начать размещать рекламу'}
                    </Typography>
                    {!search && !status && (
                        <Button
                            startDecorator={<AddRoundedIcon />}
                            onClick={() => navigate('/partner/ads/new')}
                            disabled={account?.status !== 'active'}
                        >
                            Создать кампанию
                        </Button>
                    )}
                </Sheet>
            ) : (
                <Stack spacing={1.5}>
                    {items.map((campaign) => (
                        <CampaignRow
                            key={campaign.id}
                            campaign={campaign}
                            busy={busy}
                            onOpen={(id) => navigate(`/business/${id}`)}
                            onAction={handleAction}
                        />
                    ))}
                </Stack>
            )}

            {pages > 1 && (
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 3 }}>
                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                    >
                        Назад
                    </Button>
                    <Typography level="body-sm" sx={{ alignSelf: 'center' }}>
                        {page} / {pages}
                    </Typography>
                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        disabled={page >= pages}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        Вперёд
                    </Button>
                </Stack>
            )}

            </Box>
    );
}